import * as Sentry from "@sentry/react"
import { useMutation } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { useNavigate } from "@tanstack/react-router"
import { resendSignUpCode } from "aws-amplify/auth"
import { TFunction } from "i18next"
import { useRef } from "react"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Text from "@/globalComponents/atoms/Text"
import TextField from "@/globalComponents/molecules/TextField"
import { handleAuthError } from "@/utilities/auth-error-handler"
import { requiredRule } from "@/utilities/form"

import styles from "./verify.module.css"

export const Route = createFileRoute("/auth/verify")({
  validateSearch: (
    search: Record<string, unknown>
  ): {
    email: string
    context: "sign-in" | "create-account"
  } => {
    return {
      email: (search.email as string) || "",
      context: (search.context as "sign-in" | "create-account") || "sign-in",
    }
  },
  beforeLoad: async ({ search }) => {
    // Redirect if neither email nor phone is provided
    const context = search.context
    if (!search.email) {
      throw redirect({
        to:
          context === "create-account"
            ? "/auth/create-account"
            : "/auth/sign-in",
        search: { qrLookupID: search.qrLookupID, redirect: search.redirect },
      })
    }
    if (context === "sign-in") {
      await resendSignUpCode({ username: search.email })
    }
  },
  component: RouteComponent,
})

interface VerificationFormSchema {
  digit1: string
  digit2: string
  digit3: string
  digit4: string
  digit5: string
  digit6: string
}

// Helper function to log mutation errors to Sentry
function logMutationError(
  error: any,
  context: {
    flow: string
    email?: string
    qrLookupID?: string
    additionalData?: Record<string, any>
  }
) {
  Sentry.captureException(error, {
    extra: {
      email: context.email,
      qrLookupID: context.qrLookupID,
      originalError: error.message,
      ...context.additionalData,
    },
    tags: {
      section: "auth",
      flow: context.flow,
    },
  })
}

function useVerifyMutation({
  form,
  email,
  context,
  qrLookupID,
  redirect,
  t,
}: {
  form: ReturnType<typeof useForm<VerificationFormSchema>>
  email?: string
  context?: "sign-in" | "create-account"
  qrLookupID?: string
  redirect?: string
  t: TFunction
}) {
  const navigate = useNavigate()
  const { t: tFunction } = useTranslation()

  return useMutation({
    mutationFn: async (data: VerificationFormSchema) => {
      if (!email) {
        throw new Error(t("errors.auth.emailRequiredVerification"))
      }

      const code = `${data.digit1}${data.digit2}${data.digit3}${data.digit4}${data.digit5}${data.digit6}`

      return api.auth.confirmSignUp({
        username: email,
        confirmationCode: code,
      })
    },
    onSuccess: async (result) => {
      if (
        context === "create-account" &&
        result.nextStep?.signUpStep === "COMPLETE_AUTO_SIGN_IN"
      ) {
        try {
          const autoSignInResult = await api.auth.autoSignIn()

          if (autoSignInResult.nextStep?.signInStep === "DONE") {
            if (redirect) {
              navigate({ to: redirect })
            } else if (qrLookupID) {
              navigate({
                to: "/qr/$qrLookupID/welcome",
                params: { qrLookupID },
              })
            } else {
              navigate({ to: "/" })
            }
            return
          }
        } catch (autoSignInError) {
          logMutationError(autoSignInError, {
            flow: "auto-sign-in",
            email,
            qrLookupID,
          })
        }
      }

      // Default fallback - redirect to sign-in page
      navigate({
        to: "/auth/sign-in",
        search: { qrLookupID, redirect },
      })
    },
    onError: (err: any) => {
      logMutationError(err, {
        flow: "verification",
        email,
        qrLookupID,
        additionalData: { context },
      })

      if (err.message === tFunction("errors.auth.emailRequiredVerification")) {
        form.setError("root", { message: err.message })
      } else {
        handleAuthError(err, form, t)
      }
    },
  })
}

function useResendVerificationMutation({ email }: { email?: string }) {
  const { t } = useTranslation()

  return useMutation({
    mutationFn: async () => {
      if (!email) {
        throw new Error(t("errors.auth.emailRequiredResend"))
      }

      return api.auth.resendSignUpCode({
        username: email,
      })
    },
    onError: (err: any) => {
      logMutationError(err, {
        flow: "resend-verification",
        email,
      })
    },
  })
}

function RouteComponent() {
  const { email, context, qrLookupID, redirect } = Route.useSearch()
  const { t } = useTranslation()

  const form = useForm<VerificationFormSchema>({
    defaultValues: {
      digit1: "",
      digit2: "",
      digit3: "",
      digit4: "",
      digit5: "",
      digit6: "",
    },
    mode: "onChange",
  })

  const inputRefs = useRef<Array<HTMLInputElement | null>>([])

  const verifyMutation = useVerifyMutation({
    form,
    email,
    context,
    qrLookupID,
    redirect,
    t,
  })
  const resendMutation = useResendVerificationMutation({ email })

  const onSubmit = (data: VerificationFormSchema) => {
    form.clearErrors("root")
    verifyMutation.mutate(data)
  }

  const handleResendCode = () => {
    form.clearErrors("root")
    resendMutation.mutate()
  }

  const digitFields = [
    "digit1",
    "digit2",
    "digit3",
    "digit4",
    "digit5",
    "digit6",
  ] as const

  const handlePaste = (pastedText: string) => {
    const digits = pastedText.replace(/\D/g, "").slice(0, 6)
    if (digits.length === 0) return
    form.clearErrors("root")

    // Clear all fields first
    digitFields.forEach((fieldName) => {
      form.setValue(fieldName, "", { shouldValidate: false })
    })

    // Fill fields with available digits
    for (let i = 0; i < digits.length && i < digitFields.length; i++) {
      form.setValue(digitFields[i], digits[i], {
        shouldValidate: true,
        shouldDirty: true,
      })
    }

    // Focus the next empty field, or the last field if all are filled
    const focusIndex = Math.min(digits.length, digitFields.length - 1)
    inputRefs.current[focusIndex]?.focus()
  }

  return (
    <>
      <Heading level={1} as={1}>
        {context === "create-account"
          ? t("auth.verify.headingCreateAccount")
          : t("auth.verify.headingSignIn")}
      </Heading>

      <Text level={2} className={styles.descriptionText}>
        {t("auth.verify.description")}{" "}
        {email && <strong className={styles.emailHighlight}>{email}</strong>}
        {!email && t("auth.verify.registeredContactMethod")}.
      </Text>

      <form onSubmit={form.handleSubmit(onSubmit)}>
        <fieldset
          className={styles.fieldset}
          disabled={verifyMutation.isPending}
        >
          {/* 6-Digit Code Input */}
          <div className={styles.codeInputRow}>
            {digitFields.map((fieldName, index) => (
              <Controller<VerificationFormSchema>
                key={fieldName}
                name={fieldName}
                control={form.control}
                rules={{
                  required: requiredRule(t("errors.common.required")),
                  pattern: {
                    value: /^[0-9]$/,
                    message: t("errors.common.singleDigit"),
                  },
                }}
                render={({ field: { ref, ...rest } }) => {
                  return (
                    <TextField
                      aria-label={`Verification code digit ${index + 1}`}
                    >
                      <TextField.Input
                        {...rest}
                        innerRef={(el) => {
                          inputRefs.current[index] = el
                          if (typeof ref === "function") ref(el)
                        }}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        className={styles.codeDigit}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "")

                          // Clear errors when user starts typing
                          if (form.formState.errors.root)
                            form.clearErrors("root")

                          if (val.length > 1) {
                            // Only handle paste if we're not at the last input or if current input is empty
                            if (index < digitFields.length - 1 || !rest.value) {
                              handlePaste(val)
                            } else {
                              // At the last input with existing value - just take the first digit
                              rest.onChange(val.charAt(0))
                            }
                          } else if (val.length === 1) {
                            rest.onChange(val)
                            if (index < digitFields.length - 1) {
                              inputRefs.current[index + 1]?.focus()
                            }
                          } else if (val.length === 0) {
                            rest.onChange("")
                          }
                        }}
                        onPaste={(e) => {
                          e.preventDefault()
                          const pastedText = e.clipboardData.getData("text")
                          handlePaste(pastedText)
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Backspace") {
                            if (rest.value === "") {
                              if (index > 0) {
                                inputRefs.current[index - 1]?.focus()
                              }
                            } else {
                              rest.onChange("")
                            }
                          } else if (e.key === "ArrowLeft" && index > 0) {
                            inputRefs.current[index - 1]?.focus()
                          } else if (
                            e.key === "ArrowRight" &&
                            index < digitFields.length - 1
                          ) {
                            inputRefs.current[index + 1]?.focus()
                          }
                        }}
                      />
                    </TextField>
                  )
                }}
              />
            ))}
          </div>
          {form.formState.errors.root && (
            <FieldError className="center">
              {form.formState.errors.root.message}
            </FieldError>
          )}
          <Button
            hue="secondary"
            variant="default"
            type="submit"
            isPending={verifyMutation.isPending}
          >
            {verifyMutation.isPending
              ? t("auth.verify.submittingButton")
              : t("auth.verify.submitButton")}
          </Button>
        </fieldset>
      </form>

      {/* Resend Code Link */}
      <Text level={2} className={styles.resendContainer}>
        {t("auth.verify.didntReceiveCode")}{" "}
        <Button
          hue="primary"
          presentation="link"
          onPress={handleResendCode}
          isDisabled={resendMutation.isPending || verifyMutation.isPending}
          style={{
            textDecorationLine: "underline",
            opacity:
              resendMutation.isPending || verifyMutation.isPending ? 0.5 : 1,
          }}
        >
          {resendMutation.isPending
            ? t("auth.verify.resendingCode")
            : t("auth.verify.resendCode")}
        </Button>
        .
      </Text>
    </>
  )
}
